import { getApiGatewayUrls } from './apiGatewayUtils';

const PRODUCTION_SDK_URL = 'https://main.live.built-in-support.concursolutions.com/dsx-everywhere-sdk/sdk-react-dsx-everywhere.js';
const TESTING_SDK_URL = 'https://main.feat-test.built-in-support.concursolutions.com/dsx-everywhere-sdk/sdk-react-dsx-everywhere.js';

const HOST_TO_SDK_URL = {
    localhost: TESTING_SDK_URL,
    'us2.concursolutions.com': PRODUCTION_SDK_URL,
    'integration.concursolutions.com': TESTING_SDK_URL,
    'eu2.concursolutions.com': PRODUCTION_SDK_URL,
    'apj1.concursolutions.com': PRODUCTION_SDK_URL,
};

const getSdkUrl = () => HOST_TO_SDK_URL[window?.location?.hostname] ?? TESTING_SDK_URL;

let sdk$;
export function getSDK() {
    if (!sdk$) {
        const sdkUrl = getSdkUrl();
        sdk$ = new Promise((resolve, reject) => {
            const el = document.createElement('script');
            const container = document.head || document.body;
            el.async = true;
            el.src = sdkUrl;
            el.addEventListener('load', () => resolve(window['sdk-react-dsx-everywhere']));
            el.addEventListener('error', () =>
                reject(new Error(`Failed to load the script ${sdkUrl}`)),
            );
            container.appendChild(el);
        });
    }
    return sdk$;
}

function getProxyServiceUrl() {
    if (window.location.hostname === 'localhost') {
        return `${window.location.protocol}//${window.location.hostname}:${window.location.port}/entitymanagement/v4/bis/token`;
    }
    return `https://${getApiGatewayUrls().apigwBrowserUrl}/entitymanagement/v4/bis/token`;
}

const BIS_ALLOWED_FIELDS = ['appId', 'pageMainId', 'pageSubId', 'moduleId', 'objectId', 'PPMSIDs', 'achComponent'];

// The themes Built-In Support accepts. App themeName values outside this set
// (concur_gateway, concur_default, complete/complete_dark playground themes) are
// mapped to their closest supported base where one exists, else omitted so BIS
// keeps its own default rather than receiving an unknown theme.
const BIS_SUPPORTED_THEMES = new Set([
    'sap_horizon',
    'sap_horizon_dark',
    'sap_horizon_hcb',
    'sap_horizon_hcw',
    'sap_fiori_3',
    'sap_fiori_3_dark',
]);

const THEME_ALIAS = {
    complete: 'sap_horizon',
    complete_dark: 'sap_horizon_dark',
    concur_default: 'sap_horizon',
};

export function toBisTheme(themeName) {
    if (!themeName) return undefined;
    if (BIS_SUPPORTED_THEMES.has(themeName)) return themeName;
    return THEME_ALIAS[themeName];
}

let bisCreated = false;
let bisCleanup = null;
let bisMessageListener = null;
let bisCloseCallback = null;

export function onBisClose(fn) {
    bisCloseCallback = fn;
    return () => { bisCloseCallback = null; };
}

export async function loadSDK(headerBottom, bisConfig = {}, companyUUID, { lang, themeName } = {}) {
    if (bisCreated) return;
    const sdk = await getSDK();
    sdk.create({
        topOffset: `${headerBottom}px`,
        baseUrl: new URL(getSdkUrl()).origin,
        zIndex: 999,
        useIcon: false,
        resizable: false,
        onClose: () => bisCloseCallback?.(),
        getProductContext: async () => ({
            isRoleBasedIntegration: true,
            productName: 'SAP Concur',
            helpPortalProductID: 'sap-concur',
            productFallbackACH: 'CON-CA-BIS',
            productSupportACH: 'CON-CA-BIS',
            PPMSIDs: window?.cnqr?.coreui?.bisRouteInfo?.PPMSIDs ?? bisConfig.PPMSIDs ?? '',
            language: lang || 'en',
            ...(toBisTheme(themeName) ? { theme: toBisTheme(themeName) } : {}),
        }),
        getSystemContext: async () => ({
            globalTenantID: companyUUID?.toUpperCase() || '',
            systemRole: 'CONCUR',
        }),
        getApplicationContext: async () => {
            const bisRouteInfo = window?.cnqr?.coreui?.bisRouteInfo;
            return {
                appId: bisRouteInfo?.appId ?? '',
                applicationName: bisRouteInfo?.appId ?? '',
                pageMainId: bisRouteInfo?.pageMainId ?? '',
                pageSubId: bisRouteInfo?.pageSubId ?? '',
                moduleId: bisRouteInfo?.moduleId ?? '',
                objectId: bisRouteInfo?.objectId ?? '',
                achComponent: bisRouteInfo?.achComponent ?? 'CON-CA-BIS',
                appUrl: window.location.pathname,
            };
        },
        getAdditionalContext: async () => {
            const match = window.location.pathname.match(/expense\/reports\/([^/?#]+)/);
            return match ? { supportInformation: { reportId: match[1] } } : {};
        },
    });
    bisCreated = true;

    bisCleanup = sdk.subscribeSecureTokenExchange({
        getToken: async () => {
            const response = await fetch(getProxyServiceUrl(), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                },
                body: '{}',
            });
            if (!response.ok) {
                const errorBody = await response.json();
                throw Object.assign(new Error('Failed to retrieve BIS token'), {
                    errorResponseBody: errorBody,
                });
            }
            const data = await response.json();
            return data.token;
        },
    });

    bisMessageListener = (event) => {
        if (event.origin === `${window.location.protocol}//${window.location.host}`
            && event.data?.type === 'BIS_ROUTE_INFO_CHANGE') {
            refreshContext(event.data);
        }
    };
    window.addEventListener('message', bisMessageListener);
}

export async function refreshContext(bisRouteInfo) {
    if (!bisCreated) return;
    const sdk = await getSDK();
    const context = Object.fromEntries(
        BIS_ALLOWED_FIELDS
            .map((key) => [key, bisRouteInfo[key]]),
    );
    await sdk.refreshContext(async () => ({
        ...context,
        appUrl: window.location.pathname,
    }));
}

export async function logoutSDK() {
    if (!bisCreated) return;
    const sdk = await getSDK();
    if (sdk.isInitialized()) {
        await sdk.logout();
    }
}

export async function destroySDK() {
    if (bisMessageListener) {
        window.removeEventListener('message', bisMessageListener);
        bisMessageListener = null;
    }
    if (bisCleanup) {
        bisCleanup();
        bisCleanup = null;
    }
    if (!bisCreated) return;
    const sdk = await getSDK();
    if (sdk.isInitialized()) {
        sdk.destroy();
    }
    bisCreated = false;
}
